/**
 * Features Page for "Where the Brave Things Grow"
 * 
 * Comprehensive showcase of all the book's amazing features including:
 * - Interactive reading experience with 6 unique cues
 * - Complete story structure with chapter breakdown
 * - Educational components and research-based content
 * - Family-centered design and printable resources
 * - Technical excellence and beautiful design
 * - Character-driven learning with forest friends
 * - Unique features like dynamic layout and cue collection
 */

import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Sparkles, 
  BookOpen, 
  Brain, 
  Users, 
  Printer, 
  Smartphone, 
  Palette, 
  GraduationCap,
  Heart,
  Rainbow,
  Shield,
  Leaf,
  Coffee,
  Star,
  Play,
  ArrowRight,
  CheckCircle,
  Download,
  Tablet,
  Monitor,
  Wifi,
  Bookmark,
  PaintBucket,
  Microscope,
  Target,
  FileText,
  Globe,
  Headphones,
  Clock,
  Award,
  Zap,
  RotateCcw
} from "lucide-react";

// Interactive Cues Data
const interactiveCues = [
  {
    name: "Golden Leaf",
    skill: "Mindfulness",
    description: "Breathe deeply",
    icon: Leaf,
    color: "bg-golden-100 text-golden-700 border-golden-200",
    glowColor: "shadow-golden-500/20",
    pillColor: "border-golden-400 text-golden-600"
  },
  {
    name: "Rainbow Tail",
    skill: "Emotional Awareness",
    description: "Say how you feel",
    icon: Rainbow,
    color: "bg-purple-100 text-purple-700 border-purple-200",
    glowColor: "shadow-purple-500/20",
    pillColor: "border-purple-400 text-purple-600"
  },
  {
    name: "Bravery Badge",
    skill: "Courage",
    description: "Say 'I can try!'",
    icon: Shield,
    color: "bg-blue-100 text-blue-700 border-blue-200",
    glowColor: "shadow-blue-500/20",
    pillColor: "border-blue-400 text-blue-600"
  },
  {
    name: "Sparkling Petal",
    skill: "Kindness",
    description: "Do something kind",
    icon: Sparkles,
    color: "bg-pink-100 text-pink-700 border-pink-200",
    glowColor: "shadow-pink-500/20",
    pillColor: "border-pink-400 text-pink-600"
  },
  {
    name: "Gratitude Leaf",
    skill: "Gratitude",
    description: "Share gratitude",
    icon: Heart,
    color: "bg-red-100 text-red-700 border-red-200",
    glowColor: "shadow-red-500/20",
    pillColor: "border-green-400 text-green-600"
  },
  {
    name: "Dew Cup",
    skill: "Sharing",
    description: "Think about sharing",
    icon: Coffee,
    color: "bg-orange-100 text-orange-700 border-orange-200",
    glowColor: "shadow-orange-500/20",
    pillColor: "border-cyan-400 text-cyan-600"
  }
];

// Story Chapters Data
const storyChapters = [
  {
    number: 1,
    title: "The Still Pond",
    theme: "Mindfulness & Deep Breathing",
    description: "Mila learns the power of mindful breathing and staying present in the moment",
    icon: Leaf,
    color: "bg-green-50 border-green-200",
    features: ["Pond breathing cards", "Mindfulness activities", "Breathing exercises"]
  },
  {
    number: 2,
    title: "The Colorful Chameleon",
    theme: "Emotional Awareness",
    description: "Max discovers how to name and understand his feelings with Camille",
    icon: Rainbow,
    color: "bg-purple-50 border-purple-200",
    features: ["Chameleon feelings chart", "Emotion identification", "Feeling vocabulary"]
  },
  {
    number: 3,
    title: "The Brave Bridge",
    theme: "Courage Building",
    description: "The friends learn that bravery means trying even when scared",
    icon: Shield,
    color: "bg-blue-50 border-blue-200",
    features: ["Brave steps badges", "Courage challenges", "Fear-facing strategies"]
  },
  {
    number: 4,
    title: "The Kindness Meadow",
    theme: "Kindness & Compassion",
    description: "Everyone practices showing compassion and care for others",
    icon: Sparkles,
    color: "bg-pink-50 border-pink-200",
    features: ["Kindness chain links", "Compassion activities", "Care practices"]
  },
  {
    number: 5,
    title: "The Gratitude Garden",
    theme: "Gratitude & Appreciation",
    description: "Learning to recognize and appreciate the good things in life",
    icon: Heart,
    color: "bg-red-50 border-red-200",
    features: ["Gratitude leaves", "Appreciation exercises", "Thankfulness practice"]
  },
  {
    number: 6,
    title: "The Sharing Tree",
    theme: "Sharing & Connection",
    description: "Discovering the joy of cooperation and helping friends",
    icon: Coffee,
    color: "bg-orange-50 border-orange-200",
    features: ["Need-It/Share-It cards", "Cooperation games", "Problem-solving tools"]
  }
];

// Educational Components Data
const educationalComponents = [
  {
    title: "Science Connection",
    description: "Brain science explanations in kid-friendly language",
    icon: Microscope,
    details: "Each chapter explains how the brain works and why these skills help kids focus, feel calm, and handle big emotions."
  },
  {
    title: "Try This Activity",
    description: "Guided exercises and hands-on practice",
    icon: Target,
    details: "Interactive activities that let children practice new skills immediately with step-by-step guidance."
  },
  {
    title: "Family Discussion",
    description: "Thoughtful questions for meaningful conversations",
    icon: Users,
    details: "Conversation starters that help families talk about emotions, challenges, and growth together."
  },
  {
    title: "Adult Guide",
    description: "Research-backed guidance for parents and caregivers",
    icon: FileText,
    details: "Evidence-based tips and strategies to support children's social-emotional development at home."
  }
];

// Characters Data
const characters = [
  {
    name: "Mila the Mouse",
    personality: "Curious & Energetic",
    role: "Main Character",
    description: "Leads the adventure and learns alongside young readers",
    emoji: "🐭"
  },
  {
    name: "Pip the Porcupine",
    personality: "Thoughtful & Helpful",
    role: "Problem Solver",
    description: "Helps friends think through challenges and find solutions",
    emoji: "🦔"
  },
  {
    name: "Bella the Bunny",
    personality: "Gentle & Caring",
    role: "Kindness Teacher",
    description: "Shows how to be kind and compassionate to others",
    emoji: "🐰"
  },
  {
    name: "Grandpa Tortoise",
    personality: "Wise & Patient",
    role: "Life Mentor",
    description: "Shares wisdom and life lessons with gentle guidance",
    emoji: "🐢"
  },
  {
    name: "Camille the Chameleon",
    personality: "Colorful & Expressive",
    role: "Emotion Guide",
    description: "Teaches about feelings and emotional awareness",
    emoji: "🦎"
  },
  {
    name: "Wise Frog",
    personality: "Calm & Mindful",
    role: "Mindfulness Teacher",
    description: "Guides breathing exercises and mindfulness practices",
    emoji: "🐸"
  }
];

// Technical Features Data
const technicalFeatures = [
  {
    title: "Cross-Device Compatibility",
    description: "Works beautifully on tablets, computers, and phones",
    icon: Globe,
    details: "Responsive design that adapts perfectly to any screen size"
  },
  {
    title: "Fast Loading",
    description: "Optimized images and animations load quickly",
    icon: Zap,
    details: "Compressed assets and efficient code for instant access"
  },
  {
    title: "Offline-Ready",
    description: "Continue reading even without internet connection",
    icon: Wifi,
    details: "Downloaded content available anytime, anywhere"
  },
  {
    title: "Bookmark System",
    description: "Automatically saves your place and progress",
    icon: Bookmark,
    details: "Smart tracking that remembers exactly where you left off"
  }
];

export default function Features() {
  // State for Interactive Reading Experience section
  const [clickedCues, setClickedCues] = useState<Set<number>>(new Set());
  const [pulsingCue, setPulsingCue] = useState<number | null>(null);

  // Function to scroll to top when buttons are clicked
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle cue click with pulse animation
  const handleCueClick = (index: number) => {
    // Add to clicked cues
    setClickedCues(prev => new Set([...prev, index]));
    
    // Trigger pulse animation
    setPulsingCue(index);
    setTimeout(() => setPulsingCue(null), 600); // Remove pulse after animation
  };

  // Reset all progress
  const handleStartOver = () => {
    setClickedCues(new Set());
    setPulsingCue(null);
  };

  return (
    <div className="min-h-screen bg-cream-50">
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 bg-gradient-to-br from-forest-600 to-golden-600 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white/20 rounded-full"></div>
          <div className="absolute top-20 right-20 w-24 h-24 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-10 left-1/4 w-16 h-16 border border-white/20 rounded-full"></div>
          <div className="absolute bottom-20 right-1/3 w-20 h-20 bg-white/10 rounded-full"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <Badge className="bg-golden-500 text-forest-900 px-4 py-2 text-lg font-semibold mb-4">
                ✨ Interactive Children's Book
              </Badge>
              <h1 className="text-5xl md:text-7xl font-bold font-playful mb-6 leading-tight">
                Where the Brave Things Grow
                <span className="block text-3xl md:text-4xl font-script text-golden-300 mt-2">
                  Features & Capabilities
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-forest-100 max-w-4xl mx-auto leading-relaxed">
                Discover every amazing feature that makes this interactive book a revolutionary learning experience for children and families.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center"
            >
              <Button size="lg" className="bg-golden-500 hover:bg-golden-600 text-forest-900 px-8 py-4 text-lg font-semibold" asChild>
                <Link to="/members" onClick={scrollToTop}>
                  <Play className="w-5 h-5 mr-2" />
                  Experience the Book
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Interactive Reading Experience */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Sparkles className="w-8 h-8 text-golden-500" />
              <h2 className="text-4xl md:text-5xl font-bold text-forest-900 font-playful">
                🌟 Interactive Reading Experience
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Six unique interactive cues guide children through meaningful learning moments throughout the story.
            </p>

            {/* Progress Bar */}
            <div className="max-w-md mx-auto mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Interactive Cues</span>
                <span className="text-sm font-medium text-gray-600">{clickedCues.size} / 6</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-forest-500 to-golden-500 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${(clickedCues.size / 6) * 100}%` }}
                ></div>
              </div>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {interactiveCues.map((cue, index) => {
              const Icon = cue.icon;
              const isClicked = clickedCues.has(index);
              const isPulsing = pulsingCue === index;
              
              return (
                <motion.div
                  key={cue.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <motion.div
                    whileHover={{ scale: 1 }} // Disable hover scaling
                    whileTap={{ scale: 1.15, rotate: 8 }} // Enhanced click feedback
                    animate={{ 
                      scale: isClicked ? 1.08 : 1,
                      rotate: isClicked ? 6 : 0,
                      y: isClicked ? -4 : 0,
                      boxShadow: isClicked ? "0 8px 25px rgba(0, 0, 0, 0.15)" : "0 2px 8px rgba(0, 0, 0, 0.1)"
                    }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  >
                    <Card 
                      className={`${cue.color} border-2 h-full cursor-pointer select-none`}
                      onClick={() => handleCueClick(index)}
                    >
                      <CardContent className="p-8 text-center">
                        <div className="w-16 h-16 mx-auto mb-6 rounded-full border-2 flex items-center justify-center bg-white relative">
                          <Icon className="w-8 h-8" />
                          {isClicked && (
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        <h3 className="text-xl font-bold mb-4">{cue.name}</h3>
                        
                        {/* White Pill-Shaped Action Container */}
                        <div className={`bg-white rounded-full border-2 px-6 py-1.5 mb-4 w-fit mx-auto ${cue.pillColor}`}>
                          <p className="text-xs font-semibold">{cue.description}</p>
                        </div>
                        
                        <div className="text-xs text-gray-600">
                          💫 Click-to-Interact Feature
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>

          {/* Start Over Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <Button
              onClick={handleStartOver}
              variant="outline"
              className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Start Over
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 text-center"
          >
            <Card className="bg-forest-50 border-forest-200 border-2 p-8">
              <div className="flex items-center justify-center gap-4 mb-6">
                <CheckCircle className="w-6 h-6 text-forest-600" />
                <h3 className="text-2xl font-bold text-forest-900">No-Scroll Reading Experience</h3>
              </div>
              <p className="text-forest-700 text-lg max-w-2xl mx-auto">
                Optimized chapters that fit perfectly on screen for distraction-free reading. Each page is carefully designed to eliminate scrolling and maintain focus.
              </p>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Complete Story Structure */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <BookOpen className="w-8 h-8 text-golden-500" />
              <h2 className="text-4xl md:text-5xl font-bold text-forest-900 font-playful">
                📚 Complete Story Structure
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Six carefully crafted chapters, each focusing on a core social-emotional skill with Mila the squirrel and her forest friends.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {storyChapters.map((chapter, index) => {
              const Icon = chapter.icon;
              return (
                <motion.div
                  key={chapter.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className={`${chapter.color} border-2  transition-all duration-300 h-full`}>
                    <CardHeader className="text-center pb-4">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white border-2 flex items-center justify-center">
                        <Icon className="w-8 h-8 text-gray-700" />
                      </div>
                      <div className="mb-2">
                        <Badge className="bg-forest-500 text-white px-3 py-1">
                          Chapter {chapter.number}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-bold text-forest-900 mb-2">
                        {chapter.title}
                      </CardTitle>
                      <Badge variant="outline" className="text-sm">
                        {chapter.theme}
                      </Badge>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-gray-600 mb-4 text-center leading-relaxed">
                        {chapter.description}
                      </p>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-forest-900 text-sm">Includes:</h4>
                        {chapter.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            {feature}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid md:grid-cols-2 gap-8"
          >
            <Card className="bg-blue-50 border-blue-200 border-2 p-8">
              <div className="flex items-center gap-4 mb-4">
                <Play className="w-8 h-8 text-blue-600" />
                <h3 className="text-2xl font-bold text-blue-900">Story Introduction</h3>
              </div>
              <p className="text-blue-700 text-lg">
                Meet Mila and her lovable forest friends before beginning the adventure. Character introductions build connection and excitement.
              </p>
            </Card>
            <Card className="bg-green-50 border-green-200 border-2 p-8">
              <div className="flex items-center gap-4 mb-4">
                <Award className="w-8 h-8 text-green-600" />
                <h3 className="text-2xl font-bold text-green-900">Story Conclusion</h3>
              </div>
              <p className="text-green-700 text-lg">
                Celebration page that reviews all the skills learned throughout the journey, reinforcing key concepts and celebrating growth.
              </p>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Educational Components */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Brain className="w-8 h-8 text-golden-500" />
              <h2 className="text-4xl md:text-5xl font-bold text-forest-900 font-playful">
                🎯 Educational Components
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Evidence-based learning components designed by child development experts to maximize educational impact.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {educationalComponents.map((component, index) => {
              const Icon = component.icon;
              return (
                <motion.div
                  key={component.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="border-2  transition-all duration-300 h-full bg-gradient-to-br from-white to-gray-50">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-forest-100 border-2 border-forest-200 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-forest-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-forest-900 mb-2">{component.title}</h3>
                          <p className="text-gray-600 mb-4 leading-relaxed">{component.description}</p>
                          <p className="text-sm text-gray-500 leading-relaxed">{component.details}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <Card className="bg-purple-50 border-purple-200 border-2 p-8">
              <div className="flex items-center justify-center gap-4 mb-6">
                <GraduationCap className="w-8 h-8 text-purple-600" />
                <h3 className="text-2xl font-bold text-purple-900">Progress Tracking</h3>
              </div>
              <p className="text-purple-700 text-lg max-w-2xl mx-auto">
                Automatic bookmark system remembers where you left off and tracks which interactive activities you've completed throughout your learning journey.
              </p>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Family-Centered Design */}
      <section className="py-20 bg-forest-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Users className="w-8 h-8 text-golden-500" />
              <h2 className="text-4xl md:text-5xl font-bold text-forest-900 font-playful">
                👪 Family-Centered Design
              </h2>
            </div>
            <p className="text-xl text-forest-700 max-w-3xl mx-auto">
              Designed for parents, caregivers, and children to enjoy together, creating meaningful shared experiences and learning moments.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Read-Together Format",
                description: "Perfect for shared reading experiences",
                icon: BookOpen,
                color: "bg-blue-100 text-blue-700 border-blue-200"
              },
              {
                title: "Discussion Guides",
                description: "Thoughtful questions for conversations",
                icon: Users,
                color: "bg-green-100 text-green-700 border-green-200"
              },
              {
                title: "Family Activities",
                description: "Exercises for daily life integration",
                icon: Heart,
                color: "bg-red-100 text-red-700 border-red-200"
              },
              {
                title: "Universal Navigation",
                description: "Intuitive controls for all ages",
                icon: Target,
                color: "bg-purple-100 text-purple-700 border-purple-200"
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className={`${feature.color} border-2  transition-all duration-300 h-full`}>
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-white border-2 flex items-center justify-center">
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                      <p className="text-sm leading-relaxed">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Printable Resources */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Printer className="w-8 h-8 text-golden-500" />
              <h2 className="text-4xl md:text-5xl font-bold text-forest-900 font-playful">
                🖨️ Printable Resources
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional-quality printable materials for each chapter that bring the book's lessons into your physical world.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                chapter: "Chapter 1",
                title: "Pond Breathing Cards",
                description: "Mindfulness practice cards with beautiful pond imagery",
                icon: Leaf,
                color: "bg-green-50 border-green-200"
              },
              {
                chapter: "Chapter 2",
                title: "Chameleon Feelings Chart",
                description: "Colorful emotion identification and tracking chart",
                icon: Rainbow,
                color: "bg-purple-50 border-purple-200"
              },
              {
                chapter: "Chapter 3",
                title: "Brave Steps Badges",
                description: "Courage-building achievement badges to collect",
                icon: Shield,
                color: "bg-blue-50 border-blue-200"
              },
              {
                chapter: "Chapter 4",
                title: "Kindness Chain Links",
                description: "Paper chain links for tracking kind acts",
                icon: Sparkles,
                color: "bg-pink-50 border-pink-200"
              },
              {
                chapter: "Chapter 5",
                title: "Gratitude Leaves",
                description: "Beautiful leaf templates for appreciation practice",
                icon: Heart,
                color: "bg-red-50 border-red-200"
              },
              {
                chapter: "Chapter 6",
                title: "Need-It/Share-It Cards",
                description: "Problem-solving cards for cooperation games",
                icon: Coffee,
                color: "bg-orange-50 border-orange-200"
              }
            ].map((resource, index) => {
              const Icon = resource.icon;
              return (
                <motion.div
                  key={resource.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className={`${resource.color} border-2  transition-all duration-300 h-full`}>
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-white border-2 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-gray-700" />
                      </div>
                      <Badge className="mb-3 bg-forest-500 text-white">
                        {resource.chapter}
                      </Badge>
                      <h3 className="text-lg font-bold text-forest-900 mb-2">{resource.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed mb-4">{resource.description}</p>
                      <Button size="sm" variant="outline" className="w-full" disabled>
                        <Download className="w-4 h-4 mr-2" />
                        Downloadable PDF
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technical Excellence */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Smartphone className="w-8 h-8 text-golden-500" />
              <h2 className="text-4xl md:text-5xl font-bold text-forest-900 font-playful">
                📱 Technical Excellence
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built with cutting-edge technology to deliver a seamless, fast, and reliable experience across all devices.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {technicalFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="border-2  transition-all duration-300 h-full bg-white">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-forest-100 border-2 border-forest-200 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-forest-600" />
                      </div>
                      <h3 className="text-lg font-bold text-forest-900 mb-2">{feature.title}</h3>
                      <p className="text-sm text-gray-600 mb-3 leading-relaxed">{feature.description}</p>
                      <p className="text-xs text-gray-500">{feature.details}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid md:grid-cols-3 gap-8"
          >
            <Card className="bg-blue-50 border-blue-200 border-2 p-6 text-center">
              <Tablet className="w-12 h-12 mx-auto mb-4 text-blue-600" />
              <h3 className="text-lg font-bold text-blue-900 mb-2">Tablets</h3>
              <p className="text-blue-700 text-sm">Optimized for touch interaction</p>
            </Card>
            <Card className="bg-green-50 border-green-200 border-2 p-6 text-center">
              <Monitor className="w-12 h-12 mx-auto mb-4 text-green-600" />
              <h3 className="text-lg font-bold text-green-900 mb-2">Computers</h3>
              <p className="text-green-700 text-sm">Full desktop experience</p>
            </Card>
            <Card className="bg-purple-50 border-purple-200 border-2 p-6 text-center">
              <Smartphone className="w-12 h-12 mx-auto mb-4 text-purple-600" />
              <h3 className="text-lg font-bold text-purple-900 mb-2">Phones</h3>
              <p className="text-purple-700 text-sm">Mobile-responsive design</p>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Beautiful Design */}
      <section className="py-20 bg-forest-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Palette className="w-8 h-8 text-golden-500" />
              <h2 className="text-4xl md:text-5xl font-bold text-forest-900 font-playful">
                🎨 Beautiful Design
              </h2>
            </div>
            <p className="text-xl text-forest-700 max-w-3xl mx-auto">
              Every visual element carefully crafted to create a warm, inviting, and magical reading experience.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Forest-Themed Interface",
                description: "Warm, nature-inspired colors and imagery",
                icon: Leaf,
                color: "bg-green-100 text-green-700 border-green-200"
              },
              {
                title: "Child-Friendly Navigation",
                description: "Large buttons and clear visual cues",
                icon: Target,
                color: "bg-blue-100 text-blue-700 border-blue-200"
              },
              {
                title: "Smooth Animations",
                description: "Gentle transitions that enhance experience",
                icon: Sparkles,
                color: "bg-purple-100 text-purple-700 border-purple-200"
              },
              {
                title: "Accessible Typography",
                description: "Easy-to-read fonts for young readers",
                icon: FileText,
                color: "bg-orange-100 text-orange-700 border-orange-200"
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className={`${feature.color} border-2  transition-all duration-300 h-full`}>
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-white border-2 flex items-center justify-center">
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                      <p className="text-sm leading-relaxed">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Character-Driven Learning */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Heart className="w-8 h-8 text-golden-500" />
              <h2 className="text-4xl md:text-5xl font-bold text-forest-900 font-playful">
                🌈 Character-Driven Learning
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the lovable forest friends who guide children through their social-emotional learning journey.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {characters.map((character, index) => (
              <motion.div
                key={character.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="border-2  transition-all duration-300 h-full bg-gradient-to-br from-white to-gray-50">
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl mb-4">{character.emoji}</div>
                    <h3 className="text-xl font-bold text-forest-900 mb-2">{character.name}</h3>
                    <Badge className="mb-3 bg-forest-100 text-forest-700">
                      {character.personality}
                    </Badge>
                    <Badge variant="outline" className="block mb-4">
                      {character.role}
                    </Badge>
                    <p className="text-gray-600 text-sm leading-relaxed">{character.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Unique Features */}
      <section className="py-20 bg-gradient-to-br from-golden-50 to-cream-100">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Star className="w-8 h-8 text-golden-500" />
              <h2 className="text-4xl md:text-5xl font-bold text-forest-900 font-playful">
                🚀 Unique Features
              </h2>
            </div>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Innovative features that set this interactive book apart from traditional children's literature.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Dynamic Height Layout",
                description: "Text and images adjust perfectly to screen size",
                icon: Monitor,
                color: "bg-blue-50 border-blue-200"
              },
              {
                title: "Cue Collection System",
                description: "Track which interactive activities you've completed",
                icon: CheckCircle,
                color: "bg-green-50 border-green-200"
              },
              {
                title: "Continue Reading",
                description: "Smart bookmark takes you exactly where you left off",
                icon: Bookmark,
                color: "bg-purple-50 border-purple-200"
              },
              {
                title: "Multiple Entry Points",
                description: "Access stories, activities, or resources directly",
                icon: Target,
                color: "bg-orange-50 border-orange-200"
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className={`${feature.color} border-2  transition-all duration-300 h-full`}>
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-white border-2 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-gray-700" />
                      </div>
                      <h3 className="text-lg font-bold text-forest-900 mb-2">{feature.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Join Our Mission */}
      <section className="py-20 bg-gradient-to-br from-forest-600 to-golden-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold font-playful mb-6">
              Join Our Mission
            </h2>
            <p className="text-xl text-forest-100 mb-8 max-w-2xl mx-auto">
              We're building a community of families committed to nurturing emotional intelligence in children. Share your thoughts and help us create even better learning experiences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-golden-500 hover:bg-golden-600 text-forest-900 px-8 py-4 text-lg font-semibold" asChild>
                <Link to="/members" onClick={scrollToTop}>
                  <Play className="w-5 h-5 mr-2" />
                  Start Reading Now
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-forest-900 px-8 py-4 text-lg font-semibold" asChild>
                <a href="https://step1-vh5qh35-30p1gr4cn7hy.deno.dev/feedback" target="_blank" rel="noopener noreferrer">
                  💬 Share Your Feedback
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
